import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Card,
    Badge,
    Button,
    Modal,
    Form,
    Table,
    Row,
    Col,
    InputGroup,
    Container,
    Spinner
} from 'react-bootstrap';
import { Eye, History, UserCheck, AlertTriangle, Shield, Settings, RefreshCw } from 'lucide-react';
import { useAuth } from '../AuthContext/AuthContext';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../Constants/constants';
import UserEditModal from './UserEditModal';
import './UserList.css';

const EnhancedUserList = () => {
    const { t } = useTranslation('userManagement');
    const { currentUser, isLoggedIn, authError } = useAuth();

    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState({});


    useEffect(() => {
        console.log('[UserManagement] currentUser:', currentUser);
        console.log('[UserManagement] currentUser.role:', currentUser?.role);
        console.log('[UserManagement] currentUser.role_id:', currentUser?.role_id);
        console.log('[UserManagement] permissions:', permissions);
    }, [currentUser, permissions]);

    const [selectedUser, setSelectedUser] = useState(null);
    const [showReactivateModal, setShowReactivateModal] = useState(false);
    const [showStatusHistory, setShowStatusHistory] = useState(false);
    const [showUserDetails, setShowUserDetails] = useState(false);

    const [statusHistory, setStatusHistory] = useState([]);
    const [userDetails, setUserDetails] = useState(null);

    const [reactivationReason, setReactivationReason] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [filterRole, setFilterRole] = useState('ALL');

    const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });

    const [selectedRoles, setSelectedRoles] = useState({});
    const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

    const [loading, setLoading] = useState(true);
    const [operationLoading, setOperationLoading] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const showAlert = useCallback((message, variant = 'success') => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 3000);
    }, []);

    const authenticatedAxios = useMemo(() => {
        const token = localStorage.getItem('accessToken');
        return axios.create({
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
    }, []);

    const canManageUsers = !!permissions?.can_manage_users;
    const canReactivateUsers = !!permissions?.can_reactivate_users;

    const getUserRoleId = useCallback((user) => {
        const roleId = user?.role?.id ?? user?.role_id;
        return roleId ? parseInt(roleId, 10) : null;
    }, []);

    const roleById = useMemo(() => {
        const map = new Map();
        roles.forEach(r => map.set(r.id, r));
        return map;
    }, [roles]);

    const getUserStatus = useCallback((user) => {
        const status = user?.status;
        if (status) return status;
        return user?.is_active ? 'ACTIVE' : 'INACTIVE';
    }, []);

    const formatDate = useCallback((value, withTime = false) => {
        if (!value) return '';
        const d = new Date(value);
        return withTime ? d.toLocaleString() : d.toLocaleDateString();
    }, []);

    // GET /users/ is paginated server-side (defaults to 100, capped at 1000
    // per request), so a single call silently truncates the list once the
    // org has more than 100 users. Page through skip/limit until a page
    // comes back short of the page size to reliably fetch everyone.
    const fetchAllUsers = useCallback(async () => {
        const PAGE_SIZE = 1000;
        let skip = 0;
        let allUsers = [];

        while (true) {
            const res = await axios.get(`${BASE_URL}/users/`, {
                params: { skip, limit: PAGE_SIZE }
            });
            const page = res?.data || [];
            allUsers = allUsers.concat(page);

            if (page.length < PAGE_SIZE) break;
            skip += PAGE_SIZE;
        }

        return allUsers;
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const permissionsRes = await authenticatedAxios.get(`${BASE_URL}/checkout/permissions/check`);
            const perms = permissionsRes?.data?.permissions || {};
            setPermissions(perms);

            if (!perms.can_manage_users) {
                showAlert(t('messages.noPermission'), 'danger');
                setLoading(false);
                return;
            }

            const [usersData, rolesRes] = await Promise.all([
                fetchAllUsers(),
                axios.get(`${BASE_URL}/roles/`)
            ]);

            const rolesData = rolesRes?.data || [];

            setUsers(usersData);
            setRoles(rolesData);

            // Initialize selectedRoles
            const initialRoles = {};
            usersData.forEach(u => {
                const rid = u?.role?.id ?? u?.role_id;
                initialRoles[u.id] = rid ? parseInt(rid, 10) : null;
            });
            setSelectedRoles(initialRoles);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 403) showAlert(t('messages.noPermission'), 'danger');
            else showAlert(t('messages.error'), 'danger');
            // keep some error visibility
            // eslint-disable-next-line no-console
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [authenticatedAxios, fetchAllUsers, showAlert, t]);

    useEffect(() => {
        if (!isLoggedIn) {
            setLoading(false);
            return;
        }
        fetchData();
    }, [isLoggedIn, fetchData]);

    const handleRoleChange = useCallback((userId, roleId) => {
        const parsed = parseInt(roleId, 10);

        setSelectedRoles(prev => ({ ...prev, [userId]: parsed }));

        setUsers(prev => {
            const targetRole = roleById.get(parsed);
            if (!targetRole) return prev;
            return prev.map(u => (u.id === userId ? { ...u, role_id: parsed, role: targetRole } : u));
        });
    }, [roleById]);

    const fetchUserDetails = useCallback(async (userId) => {
        setDetailsLoading(true);
        try {
            const response = await authenticatedAxios.get(`${BASE_URL}/checkout/user-info/${userId}`);
            setUserDetails(response.data);

            setSelectedUser(prev => ({
                ...(prev || {}),
                ...(response?.data?.user || {})
            }));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) showAlert(t('messages.authRequired'), 'danger');
            else if (status === 403) showAlert(t('messages.noPermissionViewDetails'), 'danger');
            else showAlert(t('messages.failedUserDetails'), 'danger');
        } finally {
            setDetailsLoading(false);
        }
    }, [authenticatedAxios, showAlert, t]);

    const fetchStatusHistory = useCallback(async (userId) => {
        try {
            const response = await authenticatedAxios.get(`${BASE_URL}/checkout/status-history/${userId}`);
            const historyData = response.data.history || response.data;
            setStatusHistory(Array.isArray(historyData) ? historyData : []);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) showAlert(t('messages.authRequired'), 'danger');
            else if (status === 403) showAlert(t('messages.noPermissionStatusHistory'), 'danger');
            else showAlert(t('messages.failedStatusHistory'), 'danger');
        }
    }, [authenticatedAxios, showAlert, t]);

    const updateUser = useCallback(async (userId, updateData) => {
        if (!canManageUsers) {
            showAlert(t('messages.noPermissionUpdateUsers'), 'danger');
            return;
        }

        setOperationLoading(true);

        try {
            const currentIndex = users.findIndex(u => u.id === userId);
            if (currentIndex === -1) throw new Error('User not found');

            const current = users[currentIndex];
            let updated = { ...current };

            // Status change
            if (Object.prototype.hasOwnProperty.call(updateData, 'is_active')) {
                await authenticatedAxios.post(
                    `${BASE_URL}/checkout/manual-user-activation/${userId}`,
                    {
                        is_active: updateData.is_active,
                        reason: t('messages.activationChangedReason')
                    }
                );
                updated.is_active = updateData.is_active;
            }

            // Role change
            if (Object.prototype.hasOwnProperty.call(updateData, 'role_id')) {
                const roleId = parseInt(updateData.role_id, 10);
                if (!roleId || Number.isNaN(roleId)) throw new Error('Invalid role_id');

                const targetRole = roleById.get(roleId);
                if (!targetRole) throw new Error('Role not found');

                const completeUpdateData = {
                    role_id: roleId,
                    is_active: updated.is_active
                };

                await authenticatedAxios.put(`${BASE_URL}/users/${userId}`, completeUpdateData);

                updated.role_id = roleId;
                updated.role = targetRole;

                setSelectedRoles(prev => ({ ...prev, [userId]: roleId }));
            }

            setUsers(prev => {
                const copy = [...prev];
                copy[currentIndex] = updated;
                return copy;
            });

            showAlert(t('messages.userUpdated'), 'success');

            setTimeout(async () => {
                await fetchData();
            }, 1000);

            if (showUserDetails && selectedUser?.id === userId) {
                await fetchUserDetails(userId);
            }
        } catch (err) {
            const status = err?.response?.status;
            const serverDetail = err?.response?.data?.detail;
            const message = serverDetail || err?.message || t('messages.updateError');

            if (status === 401) showAlert(t('messages.authFailedLoginAgain'), 'danger');
            else if (status === 403) showAlert(t('messages.noPermissionAction'), 'danger');
            else if (status === 404) showAlert(t('messages.userOrRoleNotFound'), 'danger');
            else if (status === 400) showAlert(t('messages.invalidRequest', { message }), 'danger');
            else showAlert(message, 'danger');

            // revert role select if needed
            if (Object.prototype.hasOwnProperty.call(updateData, 'role_id')) {
                const originalRoleId = currentUser?.role?.id || currentUser?.role_id;
                setSelectedRoles(prev => ({ ...prev, [userId]: originalRoleId }));
            }

            setTimeout(async () => {
                await fetchData();
            }, 500);
        } finally {
            setOperationLoading(false);
        }
    }, [
        authenticatedAxios,
        canManageUsers,
        currentUser?.role?.id,
        currentUser?.role_id,
        fetchData,
        fetchUserDetails,
        roleById,
        selectedUser?.id,
        showAlert,
        showUserDetails,
        t,
        users
    ]);

    const handleShowUserDetails = useCallback((user) => {
        setSelectedUser(user);
        fetchUserDetails(user.id);
        setShowUserDetails(true);
    }, [fetchUserDetails]);

    const handleShowStatusHistory = useCallback((user) => {
        setSelectedUser(user);
        fetchStatusHistory(user.id);
        setShowStatusHistory(true);
    }, [fetchStatusHistory]);

    const handleShowReactivateModal = useCallback((user) => {
        if (!canReactivateUsers) {
            showAlert(t('messages.noPermissionReactivate'), 'danger');
            return;
        }
        setSelectedUser(user);
        setReactivationReason('');
        setShowReactivateModal(true);
    }, [canReactivateUsers, showAlert, t]);

    const handleReactivateUser = useCallback(async () => {
        if (!selectedUser || !reactivationReason.trim()) {
            showAlert(t('messages.provideReactivationReason'), 'danger');
            return;
        }
        if (!canReactivateUsers) {
            showAlert(t('messages.noPermissionReactivate'), 'danger');
            return;
        }

        setOperationLoading(true);
        try {
            const response = await authenticatedAxios.post(
                `${BASE_URL}/checkout/reactivate-user/${selectedUser.id}`,
                {
                    user_id: selectedUser.id,
                    reason: reactivationReason,
                    reset_checkout: true,
                    reset_equipment: true
                }
            );

            showAlert(
                t('messages.userReactivated', {
                    username: selectedUser.username,
                    byRole: response?.data?.reactivated_by_role
                }),
                'success'
            );

            setShowReactivateModal(false);
            setSelectedUser(null);
            setReactivationReason('');

            await fetchData();
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) showAlert(t('messages.authRequired'), 'danger');
            else if (status === 403) showAlert(t('messages.noPermissionReactivate'), 'danger');
            else showAlert(err?.response?.data?.detail || t('messages.failedReactivate'), 'danger');
        } finally {
            setOperationLoading(false);
        }
    }, [
        authenticatedAxios,
        canReactivateUsers,
        fetchData,
        reactivationReason,
        selectedUser,
        showAlert,
        t
    ]);

    const refreshUserDetails = useCallback(async () => {
        if (selectedUser) await fetchUserDetails(selectedUser.id);
    }, [fetchUserDetails, selectedUser]);

    const getStatusBadge = useCallback((user, source = 'list') => {
        let status;
        let isActive;

        if (source === 'details' && userDetails?.user) {
            status = userDetails.user.status;
            isActive = userDetails.user.is_active;
        } else {
            status = user?.status;
            isActive = user?.is_active;
        }

        const finalStatus = status || (isActive ? 'ACTIVE' : 'INACTIVE');
        const variants = { ACTIVE: 'success', INACTIVE: 'secondary', SUSPENDED: 'warning' };

        return (
            <Badge
                bg={variants[finalStatus] || 'light'}
                title={t('tooltips.statusBadge', {
                    status: finalStatus,
                    active: isActive ? t('common.yes') : t('common.no')
                })}
            >
                {t(`status.${finalStatus.toLowerCase()}`, finalStatus)}
            </Badge>
        );
    }, [t, userDetails]);

    const getRoleBadge = useCallback((user) => {
        const roleId = getUserRoleId(user);
        const role = roleId ? roleById.get(roleId) : null;
        return <Badge bg="info">{role?.name || t('common.noRole')}</Badge>;
    }, [getUserRoleId, roleById, t]);

    const getPermissionBadge = useCallback((userRoleName) => {
        const isPrivileged = ['HR', 'ADMIN', 'ICT', 'SECURITY', 'MANAGEMENT'].includes(userRoleName);
        return isPrivileged ? (
            <Badge bg="warning" className="ms-1" title={t('tooltips.privilegedUser')}>
                <Shield size={12} />
            </Badge>
        ) : null;
    }, [t]);

    const filteredUsers = useMemo(() => {
        const s = searchTerm.trim().toLowerCase();
        return users.filter(user => {
            const matchesSearch =
                user.username?.toLowerCase().includes(s) ||
                user.first_name?.toLowerCase().includes(s) ||
                user.last_name?.toLowerCase().includes(s) ||
                user.email?.toLowerCase().includes(s);

            const userStatus = getUserStatus(user);
            const matchesStatus = filterStatus === 'ALL' || userStatus === filterStatus;

            const userRoleId = getUserRoleId(user);
            const matchesRole = filterRole === 'ALL' || userRoleId === parseInt(filterRole, 10);

            return matchesSearch && matchesStatus && matchesRole;
        });
    }, [filterRole, filterStatus, getUserRoleId, getUserStatus, searchTerm, users]);

    if (!isLoggedIn) {
        return (
            <Container className="text-center mt-5">
                <Alert variant="warning">
                    <AlertTriangle className="me-2" />
                    {t('messages.mustBeLoggedIn')}
                </Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                </Spinner>
                <p className="mt-2">{t('messages.loadingUserManagement')}</p>
            </Container>
        );
    }

    if (!canManageUsers) {
        return (
            <Container className="text-center mt-5">
                <Alert variant="danger">
                    <Shield className="me-2" />
                    {t('messages.accessDenied')}
                    <br />
                    <small>{t('messages.requiredRoles')}</small>
                </Alert>
            </Container>
        );
    }

    const renderCardView = () => (
        <Row className="g-3">
            {filteredUsers.map(user => {
                const userRoleId = getUserRoleId(user);
                const userRole = userRoleId ? roleById.get(userRoleId) : null;

                return (
                    <Col key={user.id} md={4}>
                        <Card className="mb-3 blue-border h-100">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <Card.Title className="mb-0">
                                        {user.first_name} {user.last_name}
                                        {getPermissionBadge(userRole?.name)}
                                    </Card.Title>

                                    <div className="d-flex gap-1">
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            onClick={() => handleShowUserDetails(user)}
                                            title={t('tooltips.viewDetails')}
                                            disabled={operationLoading}
                                        >
                                            <Eye size={16} />
                                        </Button>

                                        {canManageUsers && (
                                            <>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => handleShowStatusHistory(user)}
                                                    title={t('tooltips.statusHistory')}
                                                    disabled={operationLoading}
                                                >
                                                    <History size={16} />
                                                </Button>

                                                <UserEditModal
                                                    user={user}
                                                    roles={roles}
                                                    onUpdate={updateUser}
                                                    selectedRole={selectedRoles[user.id]}
                                                    onRoleChange={handleRoleChange}
                                                    permissions={permissions}
                                                    disabled={operationLoading}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-2">
                                    <strong>{t('form.username')}:</strong> {user.username}
                                </div>
                                <div className="mb-2">
                                    <strong>{t('form.email')}:</strong> {user.email}
                                </div>
                                <div className="mb-2">
                                    <strong>{t('form.role')}:</strong> {getRoleBadge(user)}
                                </div>
                                <div className="mb-2">
                                    <strong>{t('form.status')}:</strong> {getStatusBadge(user, 'list')}
                                </div>

                                {user.checkout_completion_date && (
                                    <div className="mb-2">
                                        <small className="text-muted">
                                            {t('messages.checkoutCompleted')}: {formatDate(user.checkout_completion_date)}
                                        </small>
                                    </div>
                                )}

                                {user.last_status_change && (
                                    <div className="mb-2">
                                        <small className="text-muted">
                                            {t('messages.lastChanged')}: {formatDate(user.last_status_change)}
                                            {user.status_changed_by && ` ${t('common.by')} ${user.status_changed_by}`}
                                        </small>
                                    </div>
                                )}

                                {canReactivateUsers && (getUserStatus(user) === 'INACTIVE') && (
                                    <Button
                                        variant="success"
                                        size="sm"
                                        className="mt-2"
                                        onClick={() => handleShowReactivateModal(user)}
                                        disabled={operationLoading}
                                    >
                                        <UserCheck size={16} className="me-1" />
                                        {operationLoading ? t('messages.processing') : t('buttons.reactivate')}
                                    </Button>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );

    const renderTableView = () => (
        <Table striped bordered hover responsive>
            <thead>
            <tr>
                <th>{t('table.name')}</th>
                <th>{t('table.username')}</th>
                <th>{t('table.email')}</th>
                <th>{t('table.role')}</th>
                <th>{t('table.status')}</th>
                <th>{t('table.lastStatusChange')}</th>
                <th>{t('table.actions')}</th>
            </tr>
            </thead>
            <tbody>
            {filteredUsers.map(user => {
                const userRoleId = getUserRoleId(user);
                const userRole = userRoleId ? roleById.get(userRoleId) : null;

                return (
                    <tr key={user.id}>
                        <td>
                            {user.first_name} {user.last_name}
                            {getPermissionBadge(userRole?.name)}
                        </td>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>{getRoleBadge(user)}</td>
                        <td>{getStatusBadge(user, 'list')}</td>
                        <td>
                            <div>{user.last_status_change ? formatDate(user.last_status_change, true) : t('common.na')}</div>
                            {user.status_changed_by && (
                                <small className="text-muted">{t('common.by')} {user.status_changed_by}</small>
                            )}
                        </td>
                        <td>
                            <div className="d-flex gap-1">
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => handleShowUserDetails(user)}
                                    title={t('tooltips.viewDetails')}
                                    disabled={operationLoading}
                                >
                                    <Eye size={16} />
                                </Button>

                                {canManageUsers && (
                                    <>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleShowStatusHistory(user)}
                                            title={t('tooltips.statusHistory')}
                                            disabled={operationLoading}
                                        >
                                            <History size={16} />
                                        </Button>

                                        {canReactivateUsers && (getUserStatus(user) === 'INACTIVE') && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => handleShowReactivateModal(user)}
                                                title={t('modal.reactivateUserTitle')}
                                                disabled={operationLoading}
                                            >
                                                <UserCheck size={16} />
                                            </Button>
                                        )}

                                        <UserEditModal
                                            user={user}
                                            roles={roles}
                                            onUpdate={updateUser}
                                            selectedRole={selectedRoles[user.id]}
                                            onRoleChange={handleRoleChange}
                                            permissions={permissions}
                                            disabled={operationLoading}
                                        />
                                    </>
                                )}
                            </div>
                        </td>
                    </tr>
                );
            })}
            </tbody>
        </Table>
    );

    return (
        <Container fluid className="p-4">
            {authError && <Alert variant="danger">{authError}</Alert>}
            {alert.show && <Alert variant={alert.variant}>{alert.message}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2>{t('header.title')}</h2>
                    <small className="text-muted">
                        {t('header.accessLevel', { role: currentUser?.role || t('common.na') })} |{' '}
                        {t('header.permissions', { level: canManageUsers ? t('header.fullAccess') : t('header.limitedAccess') })}
                        {canReactivateUsers && ` | ${t('header.canReactivate')}`}
                    </small>
                    {/*<small className="text-muted">*/}
                    {/*    {t('header.accessLevel', { role: currentUser?.role?.name || t('common.na') })} |{' '}*/}

                    {/*    {t('header.permissions', { level: canManageUsers ? t('header.fullAccess') : t('header.limitedAccess') })}*/}
                    {/*    {canReactivateUsers && ` | ${t('header.canReactivate')}`}*/}
                    {/*</small>*/}
                </div>

                <div className="d-flex gap-2">
                    <Button
                        variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('cards')}
                        disabled={operationLoading}
                    >
                        {t('buttons.cards')}
                    </Button>
                    <Button
                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('table')}
                        disabled={operationLoading}
                    >
                        {t('buttons.table')}
                    </Button>

                    <Button variant="outline-secondary" onClick={fetchData} disabled={operationLoading}>
                        {operationLoading ? <Spinner size="sm" /> : t('buttons.refresh')}
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <InputGroup>
                                <InputGroup.Text>{t('filters.searchLabel')}</InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder={t('filters.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    disabled={operationLoading}
                                />
                            </InputGroup>
                        </Col>

                        <Col md={3}>
                            <Form.Select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                disabled={operationLoading}
                            >
                                <option value="ALL">{t('filters.allStatuses')}</option>
                                <option value="ACTIVE">{t('status.active')}</option>
                                <option value="INACTIVE">{t('status.inactive')}</option>
                                <option value="SUSPENDED">{t('status.suspended')}</option>
                            </Form.Select>
                        </Col>

                        <Col md={3}>
                            <Form.Select
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                                disabled={operationLoading}
                            >
                                <option value="ALL">{t('filters.allRoles')}</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.id}>{role.name}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col md={2}>
                            <div className="text-muted">
                                {t('messages.usersCount', { count: filteredUsers.length })}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Permission Notice */}
            {permissions && Object.keys(permissions).length > 0 && (
                <Alert variant="info" className="mb-3">
                    <Settings className="me-2" />
                    <strong>{t('permissions.title')}</strong>
                    <ul className="mb-0 mt-2">
                        {canManageUsers && <li>{t('permissions.manageUsers')}</li>}
                        {canReactivateUsers && <li>{t('permissions.reactivateUsers')}</li>}
                        {permissions.can_reset_checkout_processes && <li>{t('permissions.resetCheckout')}</li>}
                        {permissions.can_force_status_changes && <li>{t('permissions.forceStatus')}</li>}
                    </ul>
                </Alert>
            )}

            {/* User Display */}
            {filteredUsers.length === 0 ? (
                <Alert variant="info">{t('messages.noUsersFound')}</Alert>
            ) : (
                viewMode === 'cards' ? renderCardView() : renderTableView()
            )}

            {/* User Details Modal */}
            <Modal show={showUserDetails} onHide={() => setShowUserDetails(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('modal.userDetailsTitle', { username: selectedUser?.username || '' })}
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="ms-2"
                            onClick={refreshUserDetails}
                            disabled={detailsLoading}
                            title={t('tooltips.refreshUserDetails')}
                        >
                            <RefreshCw size={16} className={detailsLoading ? 'spin' : ''} />
                        </Button>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {detailsLoading ? (
                        <div className="text-center">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">{t('messages.loadingUserDetails')}</span>
                        </div>
                    ) : userDetails ? (
                        <Row>
                            <Col md={6}>
                                <h5>{t('userDetail.personalInformation')}</h5>
                                <p><strong>{t('userDetail.name')}:</strong> {userDetails.user.first_name} {userDetails.user.last_name}</p>
                                <p><strong>{t('form.username')}:</strong> {userDetails.user.username}</p>
                                <p><strong>{t('form.email')}:</strong> {userDetails.user.email}</p>
                                <p><strong>{t('form.status')}:</strong> {getStatusBadge(userDetails.user, 'details')}</p>

                                {userDetails.user.last_status_change && (
                                    <p><strong>{t('userDetail.lastStatusChange')}:</strong> {formatDate(userDetails.user.last_status_change, true)}</p>
                                )}
                                {userDetails.user.status_changed_by && (
                                    <p><strong>{t('userDetail.changedBy')}:</strong> {userDetails.user.status_changed_by}</p>
                                )}
                            </Col>

                            <Col md={6}>
                                <h5>{t('userDetail.checkoutInformation')}</h5>

                                {userDetails.checkout_process ? (
                                    <>
                                        <p>
                                            <strong>{t('userDetail.ictCheckout')}:</strong>
                                            <Badge bg={userDetails.checkout_process.ict_checkout_status ? 'success' : 'warning'} className="ms-2">
                                                {userDetails.checkout_process.ict_checkout_status ? t('status.completed') : t('status.pending')}
                                            </Badge>
                                        </p>

                                        <p>
                                            <strong>{t('userDetail.securityCheckout')}:</strong>
                                            <Badge bg={userDetails.checkout_process.security_checkout_status ? 'success' : 'warning'} className="ms-2">
                                                {userDetails.checkout_process.security_checkout_status ? t('status.completed') : t('status.pending')}
                                            </Badge>
                                        </p>

                                        <p>
                                            <strong>{t('userDetail.hrCheckout')}:</strong>
                                            <Badge bg={userDetails.checkout_process.hr_checkout_status ? 'success' : 'warning'} className="ms-2">
                                                {userDetails.checkout_process.hr_checkout_status ? t('status.completed') : t('status.pending')}
                                            </Badge>
                                        </p>

                                        <p>
                                            <strong>{t('userDetail.processComplete')}:</strong>
                                            <Badge bg={userDetails.checkout_process.is_checkout_complete ? 'success' : 'secondary'} className="ms-2">
                                                {userDetails.checkout_process.is_checkout_complete ? t('common.yes') : t('common.no')}
                                            </Badge>
                                        </p>
                                    </>
                                ) : (
                                    <p>{t('messages.noActiveCheckout')}</p>
                                )}

                                {userDetails.requested_by && (
                                    <div className="mt-3">
                                        <small className="text-muted">
                                            {t('userDetail.requestedBy', {
                                                role: userDetails.requested_by.role,
                                                username: userDetails.requested_by.username
                                            })}
                                        </small>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    ) : (
                        <Alert variant="warning">{t('messages.failedUserDetails')}</Alert>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowUserDetails(false)}>
                        {t('buttons.close')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reactivation Modal */}
            <Modal show={showReactivateModal} onHide={() => setShowReactivateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <AlertTriangle className="me-2 text-warning" />
                        {t('modal.reactivateUserTitle')}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedUser && (
                        <>
                            <Alert variant="warning">
                                <Alert.Heading>{t('modal.confirmReactivation')}</Alert.Heading>
                                <p>{t('modal.reactivationWarning')}</p>
                                <p><strong>{selectedUser.first_name} {selectedUser.last_name} ({selectedUser.username})</strong></p>
                                <hr />
                                <p>{t('modal.reactivationEffects')}</p>
                                <ul>
                                    <li>{t('modal.changeStatusEffect')}</li>
                                    <li>{t('modal.resetCheckoutEffect')}</li>
                                    <li>{t('modal.resetEquipmentEffect')}</li>
                                </ul>
                                <p className="mb-0">
                                    <small className="text-muted">
                                        {t('modal.authorizedBy', { role: currentUser?.role?.name, username: currentUser?.username })}
                                    </small>
                                </p>
                            </Alert>

                            <Form.Group>
                                <Form.Label>{t('form.reactivationReason')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={reactivationReason}
                                    onChange={(e) => setReactivationReason(e.target.value)}
                                    placeholder={t('form.reactivationReasonPlaceholder')}
                                    required
                                    disabled={operationLoading}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReactivateModal(false)} disabled={operationLoading}>
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleReactivateUser}
                        disabled={!reactivationReason.trim() || operationLoading}
                    >
                        <UserCheck className="me-1" />
                        {operationLoading ? t('messages.processing') : t('buttons.confirmReactivation')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Status History Modal */}
            <Modal show={showStatusHistory} onHide={() => setShowStatusHistory(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <History className="me-2" />
                        {t('modal.statusHistoryTitle', { username: selectedUser?.username || '' })}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {statusHistory.length === 0 ? (
                        <Alert variant="info">{t('messages.noStatusHistory')}</Alert>
                    ) : (
                        <Table striped bordered hover>
                            <thead>
                            <tr>
                                <th>{t('statusHistory.date')}</th>
                                <th>{t('statusHistory.from')}</th>
                                <th>{t('statusHistory.to')}</th>
                                <th>{t('statusHistory.changedBy')}</th>
                                <th>{t('statusHistory.reason')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {statusHistory.map((h, idx) => (
                                <tr key={h.id || idx}>
                                    <td>{formatDate(h.changed_at, true)}</td>
                                    <td>{h.previous_status ? <Badge bg="secondary">{h.previous_status}</Badge> : t('common.na')}</td>
                                    <td>
                                        <Badge bg={h.new_status === 'ACTIVE' ? 'success' : 'secondary'}>
                                            {t(`status.${String(h.new_status).toLowerCase()}`, h.new_status)}
                                        </Badge>
                                    </td>
                                    <td>{h.changed_by}</td>
                                    <td>{h.change_reason || t('messages.noReasonProvided')}</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatusHistory(false)}>
                        {t('buttons.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default EnhancedUserList;